import React from 'react';
import PropTypes from 'prop-types';

const Skeleton = ({ 
  type = 'rectangle', 
  width, 
  height, 
  className = '', 
  count = 1,
  circle = false,
  children 
}) => {
  // Style for the base skeleton element
  const baseStyle = "bg-gray-200 animate-pulse relative overflow-hidden";
  
  // Add shimmer effect
  const shimmer = (
    <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent" />
  );

  // Style based on type
  const getTypeStyle = () => {
    switch(type) {
      case 'text':
        return `h-4 rounded-md ${width ? width : 'w-full'}`;
      case 'title':
        return `h-7 rounded-md ${width ? width : 'w-3/4'}`;
      case 'avatar':
        return `h-12 w-12 rounded-full`;
      case 'button':
        return `h-10 rounded-md ${width ? width : 'w-24'}`;
      case 'card':
        return `rounded-lg ${width ? width : 'w-full'} ${height ? height : 'h-64'}`;
      case 'image':
        return `rounded-md ${width ? width : 'w-full'} ${height ? height : 'h-40'}`;
      case 'circle':
        return `rounded-full ${width ? width : 'w-12'} ${height ? height : 'h-12'}`;
      default:
        return `${width ? width : 'w-full'} ${height ? height : 'h-24'} ${circle ? 'rounded-full' : 'rounded-md'}`;
    }
  };

  const SkeletonItem = () => (
    <div className={`${baseStyle} ${getTypeStyle()} ${className}`}>
      {shimmer}
      {children}
    </div>
  );

  if (count > 1) {
    return (
      <div className="flex flex-col gap-2">
        {[...Array(count)].map((_, index) => (
          <SkeletonItem key={index} />
        ))}
      </div>
    );
  }

  return <SkeletonItem />;
};

Skeleton.propTypes = {
  type: PropTypes.oneOf(['text', 'title', 'avatar', 'button', 'card', 'image', 'circle', 'rectangle']),
  width: PropTypes.string,
  height: PropTypes.string,
  className: PropTypes.string,
  count: PropTypes.number,
  circle: PropTypes.bool,
  children: PropTypes.node
};

// Predefined skeleton patterns for common UI components
export const ProductCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden">
    {/* Product image area with shimmer */}
    <div className="relative h-48">
      <Skeleton type="image" height="h-48" />
      
      {/* Status ribbon placeholder */}
      <div className="absolute top-0 left-0 m-3">
        <Skeleton type="text" width="w-12" height="h-5" className="rounded-l-md" />
      </div>
      
      {/* Discount badge placeholder */}
      <div className="absolute top-0 right-0">
        <Skeleton type="circle" width="w-16" height="h-16" className="rounded-bl-full" />
      </div>
    </div>

    <div className="p-4">
      {/* Product info */}
      <div className="mb-4">
        <Skeleton type="title" width="w-3/4" />
        <Skeleton type="text" width="w-1/2" className="mt-1" />
        
        {/* Price section */}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2">
            <Skeleton type="text" width="w-20" height="h-7" />
            <Skeleton type="text" width="w-16" height="h-5" />
          </div>
          <Skeleton type="text" width="w-12" height="h-6" className="rounded-full" />
        </div>
        
        {/* Stock status */}
        <div className="mt-2 flex items-center gap-1.5">
          <Skeleton type="circle" width="w-2" height="h-2" />
          <Skeleton type="text" width="w-16" height="h-5" />
        </div>
      </div>
      
      {/* Action buttons */}
      <div className="flex gap-2">
        <Skeleton type="button" width="w-full" height="h-10" className="rounded-full" />
        <Skeleton type="circle" width="w-10" height="h-10" />
      </div>
    </div>
  </div>
);

export const ProfileSkeleton = () => (
  <div className="flex flex-col md:flex-row gap-4 bg-white p-6 rounded-lg shadow-sm">
    <Skeleton type="avatar" width="w-20" height="h-20" />
    <div className="flex-1 space-y-3">
      <Skeleton type="title" />
      <Skeleton type="text" width="w-1/2" />
      <Skeleton type="text" count={3} />
    </div>
  </div>
);

export const TableRowSkeleton = ({ columns = 4 }) => (
  <div className="flex gap-2 items-center py-3">
    {[...Array(columns)].map((_, index) => (
      <Skeleton 
        key={index} 
        type="text" 
        width={index === 0 ? "w-1/6" : index === columns - 1 ? "w-1/12" : "w-1/4"} 
      />
    ))}
  </div>
);

export const TeamMemberSkeleton = () => (
  <div className="bg-white p-8 border border-neutral-200">
    {/* Image skeleton */}
    <Skeleton 
      type="image"
      height="h-64" 
      className="mb-6 aspect-square" 
    />
    
    {/* Name skeleton */}
    <Skeleton 
      type="title" 
      width="w-2/3" 
      className="mb-2" 
    />
    
    {/* Role skeleton */}
    <Skeleton 
      type="text" 
      width="w-1/2" 
      className="mb-4 bg-teal-100" 
    />
    
    {/* Details skeleton */}
    <Skeleton 
      type="text" 
      count={2} 
      className="mb-1" 
    />
  </div>
);

export const CategoryCardSkeleton = () => (
  <div className="relative">
    {/* Offset border effect */}
    <div className="absolute inset-0 border border-gray-200 translate-x-2 translate-y-2 z-0"></div>
    
    {/* Main category card skeleton */}
    <div className="relative aspect-[4/3] overflow-hidden z-10">
      <Skeleton 
        type="image"
        className="w-full h-full"
      />
      
      {/* Category name placeholder */}
      <div className="absolute inset-0 bg-neutral-900/30 flex items-center justify-center">
        <div className="px-6 py-3 border border-white/30 backdrop-blur-sm">
          <Skeleton 
            type="text"
            width="w-32"
            height="h-8"
            className="bg-white/30"
          />
        </div>
      </div>
    </div>
  </div>
);

export default Skeleton;